import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import { IOpcaoTela } from 'src/app/core/utils/permissionScreen/permissionScreen.types';
import {
  IGetRelatorioTerminalItem,
  IGetRelatorioTerminalListParams,
  IGetRelatorioTerminalListResponse
} from 'src/app/services/ReportService/report.model';
import { ReportService } from 'src/app/services/ReportService/report.service';
import { DestinatarioService } from 'src/app/services/DestinatarioService/destinatario.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';

type ReportKey =
  | 'terminal_list'
  | 'terminal'
  | 'carregamento_quantitativo'
  | 'carregamento_detalhado'
  | 'cliente_quantitativo'
  | 'cliente_detalhado'
  | 'mensal'
  | 'qtd_cliente';

type ReportOption = {
  name: string;
  value: ReportKey;
  tela: IOpcaoTela;
  mode: 'list' | 'pdf';
};

@Component({
  selector: 'app-relatorios-gerais',
  templateUrl: './relatorios-gerais.component.html',
  styleUrls: ['./relatorios-gerais.component.scss']
})
export class RelatoriosGeraisComponent implements OnInit, AfterViewInit {
  private readonly allReportOptions: ReportOption[] = [
    {
      name: 'Relatório por Terminal',
      value: 'terminal_list',
      tela: 'relatorioTerminal',
      mode: 'list'
    },
    {
      name: 'Terminal (PDF)',
      value: 'terminal',
      tela: 'relatorioTerminalPdf',
      mode: 'pdf'
    },
    {
      name: 'Carregamento - Quantitativo (PDF)',
      value: 'carregamento_quantitativo',
      tela: 'relatorioCarregamentoQuantitativo',
      mode: 'pdf'
    },
    {
      name: 'Carregamento - Detalhado (PDF)',
      value: 'carregamento_detalhado',
      tela: 'relatorioCarregamentoDetalhado',
      mode: 'pdf'
    },
    {
      name: 'Cliente - Quantitativo (PDF)',
      value: 'cliente_quantitativo',
      tela: 'relatorioClienteQuantitativo',
      mode: 'pdf'
    },
    {
      name: 'Cliente - Detalhado (PDF)',
      value: 'cliente_detalhado',
      tela: 'relatorioClienteDetalhado',
      mode: 'pdf'
    },
    {
      name: 'Mensal (PDF)',
      value: 'mensal',
      tela: 'relatorioMensalLaudos',
      mode: 'pdf'
    },
    {
      name: 'Quantidade por Cliente (PDF)',
      value: 'qtd_cliente',
      tela: 'relatorioQuantidadeClienteLaudos',
      mode: 'pdf'
    }
  ];

  form = this.fb.group({
    REPORT_TYPE: [null as ReportKey | null],
    PK_DTNR: ['' as number | string | null],
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null]
  });

  isLoading = false;
  numeroPagina = 0;
  maxPages = 1;
  quantidadeCaminhoes = 0;
  tableData: Array<any> = [];
  reportOptions: { name: string; value: ReportKey }[] = [];
  terminalOptions: { name: string; value: number }[] = [];

  pesquisaParams = {
    PAGE_NUMBER: 1,
    PAGE_SIZE: 20
  };

  tableColumns = [
    { header: 'Terminal', key: 'terminal', type: 'text' },
    { header: 'Placa Cavalo', key: 'placa', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    { header: 'Entrada', key: 'entrada', type: 'text' },
    { header: 'Saída', key: 'saida', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private destinatarioService: DestinatarioService,
    private appExportBase64: AppExportBase64
  ) {}

  get selectedReport(): ReportOption | undefined {
    const type = this.form.controls.REPORT_TYPE.value;
    return this.allReportOptions.find(o => o.value === type);
  }

  get isTerminalList(): boolean {
    return this.selectedReport?.mode === 'list';
  }

  get isPdfReport(): boolean {
    return this.selectedReport?.mode === 'pdf';
  }

  get terminalSelecionado(): boolean {
    const v = this.form.controls.PK_DTNR.value;
    return v !== null && v !== '' && v !== undefined;
  }

  get reportTitle(): string {
    return this.selectedReport?.name ?? 'Selecione um relatório';
  }

  ngOnInit(): void {
    this.reportOptions = this.allReportOptions
      .filter(o => validarPermissao(o.tela, 'consultar'))
      .map(o => ({ name: o.name, value: o.value }));

    if (this.reportOptions.length === 1) {
      this.form.controls.REPORT_TYPE.setValue(this.reportOptions[0].value);
    }

    this.destinatarioService.getDestinatarios().subscribe(response => {
      this.terminalOptions = response.Destinatarios.map(d => ({
        name: d.NM_DTNR,
        value: d.PK_DTNR
      }));
    });
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if (!this.isTerminalList) {
        this.tableData = [];
        this.quantidadeCaminhoes = 0;
        this.maxPages = 1;
        return;
      }

      if (!this.terminalSelecionado) {
        this.tableData = [];
        this.quantidadeCaminhoes = 0;
        this.maxPages = 1;
        return;
      }

      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
      this.loadTerminalData();
    });
  }

  onChangeReportType(value: ReportKey | null): void {
    this.form.controls.REPORT_TYPE.setValue(value);
    this.form.controls.PK_DTNR.setValue('');
    this.tableData = [];
    this.quantidadeCaminhoes = 0;
    this.maxPages = 1;
  }

  mudarPagina(pagina: number): void {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadTerminalData();
  }

  private createTerminalPayload(): IGetRelatorioTerminalListParams {
    const pkDtnr = this.form.controls.PK_DTNR.value;
    return {
      PK_DTNR: pkDtnr !== '' && pkDtnr !== null ? Number(pkDtnr) : undefined,
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }

  private periodParams() {
    return {
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined
    };
  }

  loadTerminalData(): void {
    if (!this.isTerminalList || !this.terminalSelecionado) return;

    this.isLoading = true;
    const payload = this.createTerminalPayload();

    this.reportService.GetRelatorioTerminal(payload).subscribe({
      next: (response: IGetRelatorioTerminalListResponse) => {
        const terminalSelecionado = this.terminalOptions.find(
          t => t.value === Number(this.form.controls.PK_DTNR.value)
        );
        this.tableData = response.ITEMS.map((item: IGetRelatorioTerminalItem) => ({
          terminal: terminalSelecionado?.name ?? '---',
          placa: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
          motorista: item.NM_MTRT ?? '---',
          entrada: formatarData(item.DT_GATE_ENTR) ?? '---',
          saida: item.DT_GATE_SAID ? formatarData(item.DT_GATE_SAID) ?? '---' : '---'
        }));
        this.maxPages = response.TOTAL_PAGES;
        this.quantidadeCaminhoes = response.TOTAL_ITEMS;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  gerarPDF(): void {
    if (!this.isPdfReport || !this.selectedReport) return;

    this.isLoading = true;
    const params = this.periodParams();
    const type = this.selectedReport.value;
    const filename = `${this.selectedReport.name}.pdf`;

    let obs$;
    if (type === 'terminal') {
      obs$ = this.reportService.GetReportTerminalLaudoPdf(params);
    } else if (type === 'carregamento_quantitativo') {
      obs$ = this.reportService.GetReportCarregamentoLaudoPdf({ ...params, FL_DETALHADO: false });
    } else if (type === 'carregamento_detalhado') {
      obs$ = this.reportService.GetReportCarregamentoLaudoPdf({ ...params, FL_DETALHADO: true });
    } else if (type === 'cliente_quantitativo') {
      obs$ = this.reportService.GetReportClienteLaudoPdf({ ...params, FL_DETALHADO: false });
    } else if (type === 'cliente_detalhado') {
      obs$ = this.reportService.GetReportClienteLaudoPdf({ ...params, FL_DETALHADO: true });
    } else if (type === 'mensal') {
      obs$ = this.reportService.GetReportMensalLaudoPdf(params);
    } else if (type === 'qtd_cliente') {
      obs$ = this.reportService.GetReportQuantidadeClienteLaudoPdf(params);
    } else {
      this.isLoading = false;
      return;
    }

    obs$.subscribe({
      next: response => {
        this.appExportBase64.downloadPDF(response.ReportBase64, filename);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    if (!this.selectedReport) return;

    if (this.isTerminalList) {
      if (!this.terminalSelecionado) return;

      this.isLoading = true;
      const payload = this.createTerminalPayload();
      const terminal = this.terminalOptions.find(
        t => t.value === Number(this.form.controls.PK_DTNR.value)
      );

      this.reportService
        .GetReportRelatorioTerminalExcel({
          PK_DTNR: payload.PK_DTNR,
          NM_DTNR: terminal?.name,
          DT_INIC: payload.DT_INIC,
          DT_FIM: payload.DT_FIM
        })
        .subscribe({
          next: response => {
            this.appExportBase64.downloadExcel(response.ReportBase64, 'Relatório Terminal.xlsx');
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
      return;
    }

    this.isLoading = true;
    const params = this.periodParams();
    const type = this.selectedReport.value;
    const filename = `${this.selectedReport.name}.xlsx`;

    let obs$;
    if (type === 'terminal') {
      obs$ = this.reportService.GetReportTerminalLaudoExcel(params);
    } else if (type === 'carregamento_quantitativo') {
      obs$ = this.reportService.GetReportCarregamentoLaudoExcel({ ...params, FL_DETALHADO: false });
    } else if (type === 'carregamento_detalhado') {
      obs$ = this.reportService.GetReportCarregamentoLaudoExcel({ ...params, FL_DETALHADO: true });
    } else if (type === 'cliente_quantitativo') {
      obs$ = this.reportService.GetReportClienteLaudoExcel({ ...params, FL_DETALHADO: false });
    } else if (type === 'cliente_detalhado') {
      obs$ = this.reportService.GetReportClienteLaudoExcel({ ...params, FL_DETALHADO: true });
    } else if (type === 'mensal') {
      obs$ = this.reportService.GetReportMensalLaudoExcel(params);
    } else if (type === 'qtd_cliente') {
      obs$ = this.reportService.GetReportQuantidadeClienteLaudoExcel(params);
    } else {
      this.isLoading = false;
      return;
    }

    obs$.subscribe({
      next: response => {
        this.appExportBase64.downloadExcel(response.ReportBase64, filename);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }
}
